import { ProviderPayableLinePaymentStatusEnum } from 'features/salesOrder/types';
import { ShowAlertState } from 'features/common/types';

export interface ServiceProviderPaymentConstraints {
	loading: boolean;
	totalPaidToProvider: number;
	hasProviderPayments: boolean;
	lockTariffProvider: boolean;
	minAllowedCost: number;
}

export const PROVIDER_PAYMENT_DISCLAIMER =
	'Ya se registraron pagos al proveedor de tarifa por este servicio. No es posible modificarlo salvo que se gestione una NDC.';

export const sumActiveProviderPayments = (
	lines: { totalPaid: number; paymentStatus: ProviderPayableLinePaymentStatusEnum }[],
): number =>
	lines
		.filter((line) => line.paymentStatus !== ProviderPayableLinePaymentStatusEnum.Cancelled)
		.reduce((sum, line) => sum + line.totalPaid, 0);

export const getServiceCostSaveError = (
	totalCost: number,
	constraints: ServiceProviderPaymentConstraints,
): string | null => {
	if (constraints.loading || !constraints.hasProviderPayments) {
		return null;
	}

	if (Number.isNaN(totalCost) || totalCost < constraints.minAllowedCost) {
		return `El costo no puede ser menor a lo ya pagado al proveedor (USD ${constraints.minAllowedCost.toFixed(2)}).`;
	}

	return null;
};

export const getTariffProviderSaveError = (
	initialTariffProviderId: string,
	submittedTariffProviderId: string,
	constraints: ServiceProviderPaymentConstraints,
): string | null => {
	if (constraints.loading || !constraints.lockTariffProvider) {
		return null;
	}

	if (initialTariffProviderId !== submittedTariffProviderId) {
		return PROVIDER_PAYMENT_DISCLAIMER;
	}

	return null;
};

export const getTariffProviderIdFromService = (service: {
	tariffProvider?: string | { id?: string };
	tariffProviderId?: string;
}): string => {
	if (typeof service.tariffProvider === 'object' && service.tariffProvider?.id) {
		return service.tariffProvider.id;
	}

	if (typeof service.tariffProvider === 'string') {
		return service.tariffProvider;
	}

	return service.tariffProviderId || 'n/a';
};

export const getTariffProviderIdFromFormData = (data: {
	tariffProvider?: string | { id?: string };
	tariffProviderId?: string;
}): string => {
	if (data.tariffProviderId) {
		return data.tariffProviderId;
	}

	if (typeof data.tariffProvider === 'string') {
		return data.tariffProvider;
	}

	if (typeof data.tariffProvider === 'object' && data.tariffProvider?.id) {
		return data.tariffProvider.id;
	}

	return 'n/a';
};

export const buildMinTotalCostRules = (
	minAllowedCost: number,
	hasProviderPayments: boolean,
	existingRules?: Record<string, unknown>,
): Record<string, unknown> => {
	if (!hasProviderPayments) {
		return existingRules ?? {};
	}

	const existingValidate = existingRules?.validate;

	return {
		...existingRules,
		validate: (value: unknown) => {
			if (typeof existingValidate === 'function') {
				const existingResult = existingValidate(value);
				if (existingResult !== true) {
					return existingResult;
				}
			}

			const numericValue = Number(value);
			if (Number.isNaN(numericValue)) {
				return true;
			}

			return (
				numericValue >= minAllowedCost ||
				`El costo no puede ser menor a lo ya pagado al proveedor (USD ${minAllowedCost.toFixed(2)}).`
			);
		},
	};
};

export const validateProviderPaymentConstraintsBeforeSave = (params: {
	constraints: ServiceProviderPaymentConstraints;
	totalCost: number;
	initialTariffProviderId: string;
	submittedTariffProviderId: string;
	onSetAlert: (values: ShowAlertState) => void;
	onSetLoading?: (value: boolean) => void;
}): boolean => {
	const tariffError = getTariffProviderSaveError(
		params.initialTariffProviderId,
		params.submittedTariffProviderId,
		params.constraints,
	);

	if (tariffError) {
		params.onSetAlert({ show: true, severity: 'error', message: tariffError });
		params.onSetLoading?.(false);
		return false;
	}

	const costError = getServiceCostSaveError(params.totalCost, params.constraints);

	if (costError) {
		params.onSetAlert({ show: true, severity: 'error', message: costError });
		params.onSetLoading?.(false);
		return false;
	}

	return true;
};
